import { Box, Grid, Typography } from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import useLocales from "src/hooks/useLocales";
import Cards from "./cards";

/**
 * Summary KPI dell'eWallet — 5 card stile dashboard:
 *  1. Available balance      (saldo prelevabile ora)
 *  2. Earned lifetime        (totale guadagnato dall'inizio)
 *  3. Total payout           (totale gia' incassato — approved + paid)
 *  4. Earned this month      (engagement, con delta % vs mese scorso)
 *  5. Volume points (VP)     (concorre al rank, tooltip esplicativo)
 *
 * Layout: 12/6/2.4 (xs/sm/md+) per simmetria a 5 colonne su desktop.
 */
const Summary = ({ summary, isLoading }) => {
  const { translate } = useLocales();

  const current = Number(summary?.earned_this_month ?? 0);
  const prev = Number(summary?.earned_previous_month ?? 0);
  const renderMonthSubInfo = () => {
    if (prev === 0 && current === 0) return null;
    if (prev === 0 && current > 0) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "success.main" }}>
          <ArrowUpwardIcon sx={{ fontSize: 12 }} />
          <span>{translate("userFinancial.eWallet.newMonth")}</span>
        </Box>
      );
    }
    const diff = current - prev;
    const pct = ((diff / prev) * 100).toFixed(0);
    const isUp = diff >= 0;
    const Icon = isUp ? ArrowUpwardIcon : ArrowDownwardIcon;
    const color = isUp ? "success.main" : "error.main";
    const sign = isUp ? "+" : "";
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color }}>
        <Icon sx={{ fontSize: 12 }} />
        <span>{sign}{pct}% {translate("userFinancial.eWallet.vsPrevMonth")}</span>
      </Box>
    );
  };

  // Wrappiamo il sub-info in Typography per coerenza tipografica nel Card.
  const monthSub = renderMonthSubInfo();
  const monthSubText = monthSub ? (
    <Typography component="span" variant="caption" sx={{ fontWeight: 600 }}>{monthSub}</Typography>
  ) : null;

  const cards = [
    {
      title: translate("userFinancial.eWallet.availableBalance"),
      icon: "mdi:wallet-outline",
      color: "#2e7d32",
      key: "balance",
      showCurrency: true,
      tooltip: translate("userFinancial.eWallet.availableBalanceTooltip"),
    },
    {
      title: translate("userFinancial.eWallet.earnedLifetime"),
      icon: "mdi:cash-multiple",
      color: "#1976d2",
      key: "earned_lifetime",
      showCurrency: true,
      tooltip: translate("userFinancial.eWallet.earnedLifetimeTooltip"),
    },
    {
      title: translate("userFinancial.eWallet.totalPayout"),
      icon: "fluent:wallet-48-regular",
      color: "#7b1fa2",
      key: "total_payout",
      showCurrency: true,
      tooltip: translate("userFinancial.eWallet.totalPayoutTooltip"),
    },
    {
      title: translate("userFinancial.eWallet.earnedThisMonth"),
      icon: "mdi:trending-up",
      color: "#ed6c02",
      key: "earned_this_month",
      showCurrency: true,
      tooltip: translate("userFinancial.eWallet.earnedThisMonthTooltip"),
      subInfo: monthSubText,
    },
    {
      title: translate("userFinancial.eWallet.volumePoints"),
      icon: "mdi:chart-donut-variant",
      color: "#0288d1",
      key: "pv",
      showCurrency: false,
      tooltip: translate("userFinancial.eWallet.volumePointsTooltip"),
      subInfo: translate("userFinancial.eWallet.volumePointsSub"),
    },
  ];

  return (
    <>
      {cards.map((c) => (
        <Grid item xs={12} sm={6} md={2.4} key={c.key}>
          <Cards {...c} total={summary?.[c.key]} isLoading={isLoading} />
        </Grid>
      ))}
    </>
  );
};

export default Summary;
